import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileCheck2 } from "lucide-react";
import { Card } from "@shared/ui/containers/Card";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { pageCopy } from "@shared/lib/pageCopy";
import { fetchMyResponses } from "./api";
import { formTypeLabel, getSurveyRouteContext } from "./routeContext";

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * Lists every submission a member has made for a single survey/eForm, so a member
 * who submitted a multi-response form more than once can pick which response to view.
 */
export function MyResponsesList(): JSX.Element {
  const { surveyId = "" } = useParams<{ surveyId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const copy = pageCopy().surveys;
  const route = getSurveyRouteContext(location.pathname);

  const query = useQuery({
    queryKey: ["surveys-my"],
    queryFn: fetchMyResponses,
  });

  const submissions = (query.data ?? [])
    .filter(
      (response) =>
        response.survey_template_id === surveyId && !!response.submitted_at,
    )
    .sort(
      (a, b) =>
        new Date(b.submitted_at ?? b.created_at).getTime() -
        new Date(a.submitted_at ?? a.created_at).getTime(),
    );

  const first = submissions[0];
  const formLabel = formTypeLabel(first?.flow_type, route.isEformRoute);
  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={first?.name ?? "Your responses"}
      subtitle={
        submissions.length
          ? `${submissions.length} ${formLabel} ${submissions.length === 1 ? "response" : "responses"}`
          : copy.subtitle
      }
      backTo={`/app/surveys/${surveyId}`}
      backLabel={`Back to ${formLabel}`}
      backBehavior="parent"
    />
  );

  if (query.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <ErrorState onRetry={() => query.refetch()} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper">
      {header}

      <div className="px-5 pt-5 pb-safe-or-6">
        {submissions.length === 0 ? (
          <EmptyState
            icon={<FileCheck2 className="h-7 w-7" strokeWidth={1.5} />}
            title="No submissions yet."
            description="Once you submit this form, your responses will appear here."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {submissions.map((response, index) => {
              const open = () =>
                navigate(
                  `/app/surveys/${surveyId}/response/${response.survey_instance_id}`,
                  {
                    state: {
                      backTo: `/app/surveys/${surveyId}/responses`,
                      backLabel: "Back to responses",
                    },
                  },
                );
              return (
                <Card
                  key={response.survey_instance_id}
                  noGrain
                  role="button"
                  tabIndex={0}
                  aria-label={`View response submitted ${response.submitted_at ? DATE_FMT.format(new Date(response.submitted_at)) : ""}`}
                  onClick={open}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      open();
                    }
                  }}
                  className="cursor-pointer p-4 transition-transform duration-fast active:scale-[0.99]"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-success/15 text-success">
                      <FileCheck2 className="h-6 w-6" strokeWidth={1.5} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-headline text-ink">
                        Response {submissions.length - index}
                      </p>
                      <p className="mt-1 text-caption text-ink-muted">
                        {response.submitted_at
                          ? `Submitted ${DATE_FMT.format(new Date(response.submitted_at))}`
                          : `Started ${DATE_FMT.format(new Date(response.created_at))}`}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
